import type { FC, ReactNode } from 'react'
import type { IntegrationFaq, IntegrationFlow, IntegrationOutcome } from '@/constants/integrations'

import { useRef } from 'react'
import { Info, Plus } from 'lucide-react'
import { cn } from '@/lib/utils'
import { initials } from '@/lib/format'
import { useInView } from '@/hooks/use-in-view'
import { Reveal } from '@/components/generals/reveal'
import { ArrowLink } from '@/components/generals/button'
import { RollingNumber } from '@/components/generals/rolling-number'
import { SectionHeading } from '@/components/generals/section-heading'

export const SystemsList: FC<{ systems: Array<string> }> = ({ systems }): ReactNode => {
    return (
        <section id='what-it-connects' aria-labelledby='what-it-connects-title' className='section-b'>
            <div className='shell'>
                <Reveal className='surface rounded-[32px] p-6 sm:p-10'>
                    <div className='flex flex-col justify-between gap-4 sm:flex-row sm:items-end'>
                        <div>
                            <p className='eyebrow flex items-center gap-2.5 text-stone'>
                                <span aria-hidden='true' className='size-1.5 rounded-full bg-ultramarine' />
                                What it connects
                            </p>

                            <h2 id='what-it-connects-title' className='mt-4 font-heading text-2xl font-semibold tracking-[-0.03em] text-ink sm:text-3xl'>
                                Connected and maintained by our team
                            </h2>
                        </div>

                        <ArrowLink href='/network'>
                            Browse the full network
                        </ArrowLink>
                    </div>

                    <ul className='mt-8 flex flex-wrap gap-2.5'>
                        {systems.map(system => (
                            <li key={system} className='inline-flex items-center gap-3 rounded-full bg-cream py-1.5 pl-1.5 pr-4'>
                                <span aria-hidden='true' className='grid size-8 place-items-center rounded-full bg-paper font-mono text-[11px] text-ink'>
                                    {initials(system)}
                                </span>

                                <span className='text-[14px] font-medium text-ink'>
                                    {system}
                                </span>

                                <span className='size-1.5 rounded-full bg-signal' aria-label='Connected' role='img' />
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </div>
        </section>
    )
}

const FlowCard: FC<{ flow: IntegrationFlow, index: number }> = ({ flow, index }): ReactNode => {
    return (
        <article className='surface flex h-full flex-col rounded-[28px] p-6 sm:p-7'>
            <p className='eyebrow text-stone'>
                {`Flow 0${index + 1}`}
            </p>

            <h3 className='mt-3 font-heading text-xl font-semibold tracking-[-0.025em] text-ink'>
                {flow.title}
            </h3>

            <div className='relative mt-6 flex-1'>
                <span aria-hidden='true' className='absolute bottom-3 left-[7px] top-3 w-px bg-[linear-gradient(to_bottom,#16140f26,#2f37ff66)]' />

                <ol className='relative space-y-4'>
                    {flow.steps.map((step, stepIndex) => (
                        <li key={step} className='flex items-start gap-4'>
                            <span
                                aria-hidden='true'
                                className={cn(
                                    'relative mt-1 size-[15px] shrink-0 rounded-full bg-paper',
                                    stepIndex === flow.steps.length - 1 ? 'border-[3px] border-ultramarine' : 'border border-ink/25'
                                )}
                            />

                            <span className='text-[15px] leading-snug text-ink-soft'>
                                {step}
                            </span>
                        </li>
                    ))}
                </ol>
            </div>

            <p className='mt-7 flex items-start gap-2.5 rounded-2xl bg-cream px-4 py-3.5 text-[13.5px] leading-relaxed text-ink-soft'>
                <Info aria-hidden='true' className='mt-0.5 size-4 shrink-0 text-ultramarine' />
                {flow.note}
            </p>
        </article>
    )
}

export const FlowsGrid: FC<{ flows: Array<IntegrationFlow> }> = ({ flows }): ReactNode => {
    return (
        <section id='flows' aria-labelledby='flows-title' className='section-y'>
            <div className='shell'>
                <SectionHeading
                    id='flows-title'
                    eyebrow="Flows"
                    title="Flows we run"
                    accent="for teams like yours."
                    intro="Each flow is built against your real bookings, then watched by our operations desk every hour after go-live."
                />

                <ul className='mt-12 grid gap-4 lg:mt-16 lg:grid-cols-3'>
                    {flows.map((flow, index) => (
                        <Reveal as='li' key={flow.title} delay={index * 100}>
                            <FlowCard flow={flow} index={index} />
                        </Reveal>
                    ))}
                </ul>
            </div>
        </section>
    )
}

export const OutcomesStrip: FC<{ outcomes: Array<IntegrationOutcome> }> = ({ outcomes }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const inView = useInView(ref, { once: true, threshold: 0.4 })

    return (
        <section id='outcomes' aria-labelledby='outcomes-title' className='section-y'>
            <div className='shell'>
                <SectionHeading
                    id='outcomes-title'
                    eyebrow="Results"
                    title="What changes"
                    accent="after go-live."
                />

                <div ref={ref} className='mt-12 lg:mt-16'>
                    <Reveal as='dl' delay={100} className='grid gap-4 md:grid-cols-3'>
                        {outcomes.map((outcome, index) => (
                            <div
                                key={outcome.label}
                                className={cn('flex flex-col justify-between rounded-[28px] p-6 sm:p-7 md:min-h-[220px]', index === 0 ? 'bg-ink text-white' : 'surface text-ink')}
                            >
                                <dt className='sr-only'>
                                    {outcome.label}
                                </dt>

                                <dd>
                                    <span className='headline block text-[clamp(2.75rem,5.4vw,4.5rem)] leading-none'>
                                        <RollingNumber value={outcome.value} active={inView} />
                                    </span>

                                    <span aria-hidden='true' className={cn('mt-5 block max-w-[16rem] text-[15px] leading-snug', index === 0 ? 'text-white/70' : 'text-ink-soft')}>
                                        {outcome.label}
                                    </span>
                                </dd>
                            </div>
                        ))}
                    </Reveal>
                </div>
            </div>
        </section>
    )
}

export const FaqList: FC<{ faqs: Array<IntegrationFaq> }> = ({ faqs }): ReactNode => {
    return (
        <section id='questions' aria-labelledby='questions-title' className='section-y'>
            <div className='shell grid gap-10 lg:grid-cols-12 lg:gap-12'>
                <SectionHeading
                    id='questions-title'
                    eyebrow="Questions"
                    title="Good"
                    accent="questions."
                    align='stack'
                    className='lg:col-span-4'
                />

                <Reveal delay={100} className='lg:col-span-8'>
                    <ul className='space-y-3'>
                        {faqs.map(faq => (
                            <li key={faq.question}>
                                <details className='group/faq surface rounded-[22px] px-5 sm:px-6'>
                                    <summary className='flex cursor-pointer list-none items-center justify-between gap-4 rounded-lg py-5 text-left font-heading text-lg font-semibold tracking-[-0.02em] text-ink sm:gap-6 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ultramarine [&::-webkit-details-marker]:hidden'>
                                        {faq.question}

                                        <span className='grid size-8 shrink-0 place-items-center rounded-full bg-cream transition-transform duration-300 group-open/faq:rotate-45'>
                                            <Plus aria-hidden='true' className='size-4' />
                                        </span>
                                    </summary>

                                    <p className='max-w-2xl pb-6 text-[15px] leading-relaxed text-ink-soft sm:pr-12'>
                                        {faq.answer}
                                    </p>
                                </details>
                            </li>
                        ))}
                    </ul>
                </Reveal>
            </div>
        </section>
    )
}
